import { useEffect } from 'react';
import { useQuery } from 'react-query';
import qs from 'qs';
import { useContact } from 'hooks/useContactsCreation/useContact';
import { useAccount } from 'hooks/useAccountsCreation/useAccount';
import { usePaymentProfile } from 'hooks/usePaymentProfileCreation/usePaymentProfile';
import { useAuth0 } from 'hooks/Auth0/Auth0';
import { useGetProduct } from 'hooks/useGetProduct/useGetProduct';
import { useFormCompleted, useFormSubmit } from 'hooks/Analytics/useAnalytics';
import { useSessionContext } from 'contexts/sessionProvider/SessionProvider';
import axios from 'helpers/axios/axios';
import { useGetAccessToken } from 'hooks/useGetAcessToken/useGetAccessToken';
import { camelToSnake } from 'helpers/camelToSnake/camelToSnake';
import { OrderItem, ProductData } from 'hooks/useGetProduct/types';
import config from '../../Config';
import { ANALYTICS_ID, REQUEST_HEADERS, SUBSCRIPTION_TYPE } from '../../constants';
import { CheckoutProps, CheckoutResponse } from './types';

export type UseCheckoutType = {
  isLoading: boolean;
  isError: boolean;
  orderNumber: string;
  isSuccess: boolean;
};

const usePreValidate = () => {
  const {
    isLoading: isAuth0Loading, error: isAuth0Error
  } = useAuth0();

  const { isLoading: isProductDataLoading, isError: isProductDataError, productData } = useGetProduct();

  return {
    isLoading: isAuth0Loading || isProductDataLoading,
    isError: isProductDataError || !!isAuth0Error,
    productData
  };
};

type CheckoutSubProduct = {
  productId: string;
  dealId: number | null;
  recurringInterval: string | null;
};

const getSelectedSubProducts = (
  productData: ProductData | undefined,
  addonIds: string[] | undefined
): CheckoutSubProduct[] => {
  if (!addonIds?.length) {
    return [];
  }

  const selectedAddonIds = new Set(addonIds);
  const subProducts = productData?.items?.[0]?.subProducts ?? [];

  return subProducts
    .map((subProduct) => subProduct.items?.[0])
    .filter((item): item is OrderItem => Boolean(item?.productId && selectedAddonIds.has(item.productId)))
    .map((item) => ({
      productId: item.productId,
      dealId: item.deal?.id ?? null,
      recurringInterval: item.deal?.recurringInterval ?? null
    }));
};

const useCheckoutInternal =
  (
    {
      contact,
      account,
      paymentProfile,
      recaptchaToken
    }: {
      contact: any,
      account: any,
      paymentProfile: any;
      recaptchaToken: string;
    }
  ): UseCheckoutType => {
    const { accessToken } = useGetAccessToken();
    const options = {
      headers: {
        ...REQUEST_HEADERS,
        Authorization: `Bearer ${accessToken}`
      }
    };
    const {
      isLoading: validateLoading,
      isError: validateError,
      productData
    } = usePreValidate();
    useFormSubmit({ elementId: ANALYTICS_ID.SUBSCRIBE_FORM });
    const { hasPurchaseSubmitted, setHasPurchaseSubmitted, activationCode, referrerDetails, quoteId, subscriptionType,
      addonIds
    } = useSessionContext();
    const url = subscriptionType === SUBSCRIPTION_TYPE.QUOTE ?
      config.API_QUOTE_CHECKOUT_ENDPOINT : config.API_CHECKOUT_ENDPOINT;
    const subProducts = getSelectedSubProducts(productData, addonIds);
    const {
      isLoading: isPurchasingLoading,
      isError: isPurchasingError,
      isSuccess,
      data
    } = useQuery(
      'checkout',
      () => axios.post<CheckoutResponse>(
        url,
        qs.stringify({
          token: recaptchaToken,
          account,
          contact,
          paymentProfile,
          deal_id: productData?.items[0]?.deal?.id,
          product_id: productData?.items[0].productId,
          ...(subProducts.length && { subProducts }),
          region: productData?.region,
          ...(activationCode && { activationCode }),
          ...(referrerDetails && { referrerDetails: referrerDetails }),
          ...(subscriptionType === SUBSCRIPTION_TYPE.QUOTE && { quoteId })
        }),
        options
      ),
      {
        enabled: (!validateLoading && !validateError && !!accessToken && !hasPurchaseSubmitted),
        retry: false,
        // This query places an order, so it must run once per checkout. Never re-run it when the page is
        // remounted, the network reconnects or the tab regains focus (e.g. returning from an app store link).
        // hasPurchaseSubmitted covers a failed attempt, which has no data and would otherwise count as stale.
        staleTime: Infinity,
        cacheTime: Infinity,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        retryOnMount: false
      }
    );

    useFormCompleted({ elementId: ANALYTICS_ID.SUBSCRIBE_FORM, isError: isPurchasingError });
    const { setSerialNumber, setProductInstanceId, setOrderNumber } = useSessionContext();
    useEffect(() => {
      if (isSuccess) {
        setSerialNumber(data?.data.serialNumber as string);
        setProductInstanceId(data?.data.productInstanceId as string);
        setOrderNumber((data?.data.purchaseId) as string);
      }
      if (isSuccess || isPurchasingError) {
        setHasPurchaseSubmitted(true);
      }
    }, [isSuccess, isPurchasingError]);

    const isLoading = validateLoading || isPurchasingLoading;
    const isError = validateError || isPurchasingError;
    const orderNumber = (data?.data.purchaseId) as string;
    return {
      isError,
      isLoading,
      orderNumber,
      isSuccess
    };
  };

export const useCheckout = (props: CheckoutProps): UseCheckoutType => {
  const contact = useContact();
  const account = useAccount();
  const paymentProfile = usePaymentProfile(props.paymentProfile);
  const recaptchaToken = props.recaptchaToken;
  return useCheckoutInternal({
    recaptchaToken,
    contact: camelToSnake(contact),
    account,
    paymentProfile
  });
};

